/**
 * The words being written on the board, between the field (`Editable.tsx`) and the style bar:
 * the field puts its runs (`shared/richText.ts`) in the page as plain text and `span`s and reads
 * them back, and while it is open it is {@link textEditor}, which the style bar asks what the
 * selected words look like and tells to change them (only those words, Miro's way).
 */
import { createSignal, type Accessor } from "solid-js";
import { runCss, tidyRuns, type Run, type RunKey } from "../../../shared/richText";

/** A part of the words, by character offsets. */
export interface Span {
  start: number;
  end: number;
}

/** The field being written in, as the style bar sees it. */
export interface TextEditor {
  /** The item whose words these are. */
  id: string;
  /** The words selected (kept while a field of the style bar has the keyboard); `null`: none yet. */
  selection: Accessor<Span | null>;
  /** The value `key` has along the selected words (`null`: it changes along them). */
  value(key: RunKey): string | null;
  /** Gives the selected words (`all`: every word) `key` = `value` (`""` unsets it), written at once (one undo). */
  apply(key: RunKey, value: string, all?: boolean): void;
  /** Takes `key` out of every word's own look (the whole item's is being set). */
  clear(key: RunKey): void;
}

const [editor, setEditor] = createSignal<TextEditor | null>(null);

/** The field open on the board, if any. */
export const textEditor = editor;

/** Makes `next` the field open (`null`: none); the field gives `null` only when it was itself. */
export function useTextEditor(next: TextEditor | null, was?: TextEditor): void {
  if (next || editor() === was) setEditor(next);
}

/** Whether there are selected words (not just a caret). */
export function hasRange(e: TextEditor | null): e is TextEditor {
  const s = e?.selection();
  return !!s && s.start !== s.end;
}

/** Writes `runs` into `field` (text and styled `span`s; a last line break kept visible). */
export function renderRuns(field: HTMLElement, runs: readonly Run[]): void {
  field.replaceChildren();
  for (const r of runs) {
    const css = runCss(r.style);
    if (!Object.keys(css).length) {
      field.append(document.createTextNode(r.text));
      continue;
    }
    const span = document.createElement("span");
    span.dataset.run = JSON.stringify(r.style);
    for (const [k, v] of Object.entries(css)) span.style.setProperty(k, v);
    span.textContent = r.text;
    field.append(span);
  }
  if (runs.length && runs[runs.length - 1]!.text.endsWith("\n")) field.append(document.createElement("br"));
}

/** The look a `span` written by {@link renderRuns} holds. */
function styleOf(el: Element, inherited: Run["style"]): Run["style"] {
  const own = el instanceof HTMLElement ? el.dataset.run : undefined;
  if (!own) return inherited;
  try {
    return { ...inherited, ...(JSON.parse(own) as Run["style"]) };
  } catch {
    return inherited;
  }
}

/**
 * The runs written in `root` (a field, or a part of it): text with the look of the `span` around
 * it, `<br>` and the browser's own lines (`<div>`) as line breaks; `whole`: `root` is the whole
 * field, whose last `<br>` only keeps an empty last line visible.
 */
export function runsOf(root: Node, whole = true): Run[] {
  const out: Run[] = [];
  const walk = (node: Node, style: Run["style"]) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) out.push({ text: child.textContent ?? "", style });
      else if (child instanceof Element) {
        if (child.tagName === "BR") out.push({ text: "\n", style });
        else {
          const block = child.tagName === "DIV" || child.tagName === "P";
          if (block && out.some((r) => r.text) && !out[out.length - 1]!.text.endsWith("\n")) out.push({ text: "\n", style });
          walk(child, styleOf(child, style));
        }
      }
    }
  };
  walk(root, {});
  if (whole && root.lastChild instanceof Element && root.lastChild.tagName === "BR") {
    const last = out[out.length - 1];
    if (last?.text === "\n") out.pop();
  }
  return tidyRuns(out);
}

/** The character offset in `field` of the point (`node`, `offset`). */
function offsetOf(field: HTMLElement, node: Node, offset: number): number {
  const range = document.createRange();
  range.setStart(field, 0);
  range.setEnd(node, offset);
  return runsOf(range.cloneContents(), false).reduce((n, r) => n + r.text.length, 0);
}

/** The words selected in `field`, by offsets (`null`: the selection is not in it). */
export function selectionIn(field: HTMLElement): Span | null {
  const sel = getSelection();
  if (!sel?.rangeCount) return null;
  const r = sel.getRangeAt(0);
  if (!field.contains(r.startContainer) || !field.contains(r.endContainer)) return null;
  return { start: offsetOf(field, r.startContainer, r.startOffset), end: offsetOf(field, r.endContainer, r.endOffset) };
}

/** The text node and offset at character `at` of a field written by {@link renderRuns}. */
function pointAt(field: HTMLElement, at: number): [Node, number] {
  const walker = document.createTreeWalker(field, NodeFilter.SHOW_TEXT);
  let left = at;
  let last: Text | null = null;
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    const len = n.data.length;
    if (left <= len) return [n, left];
    left -= len;
    last = n;
  }
  return last ? [last, last.data.length] : [field, field.childNodes.length];
}

/** Selects the words from `start` to `end` of a field written by {@link renderRuns}. */
export function select(field: HTMLElement, span: Span): void {
  const range = document.createRange();
  range.setStart(...pointAt(field, span.start));
  range.setEnd(...pointAt(field, span.end));
  const sel = getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/** Puts `text` (plain) where the caret is in `field`, replacing what is selected. */
export function insertText(field: HTMLElement, text: string): void {
  const sel = getSelection();
  if (!sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  if (!field.contains(range.startContainer)) return;
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  // A line break at the very end shows only with something after it.
  if (text.endsWith("\n") && !(field.lastChild instanceof HTMLBRElement) && runsOf(field).at(-1)?.text.endsWith("\n")) field.append(document.createElement("br"));
}
